import React from 'react';
import { branch, defaultProps, renderComponent, withHandlers } from 'recompose';
import { compose, prop } from 'ramda';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import PropTypes from 'prop-types';

import Benefits from 'app/features/registrationPromo/components/Benefits';
import S from 'app/features/registrationPromo/RegistrationPromo.styled';
import connect from 'app/libs/connect';
import Meta from 'app/features/landing/meta/components/Meta';
import LoginFreeTrialPromptRedirect from 'app/router/LoginFreeTrialPromptRedirect';
import ProductCta from 'app/features/landing/components/ProductCta';
import { FAN_PACK_FREE_TRIAL_SKU } from 'app/constants/freeTrial';
import {
  isDirectToPay,
  isUserAnonymous,
  isUserPremium,
} from 'app/features/user/selectors';
import AlreadyPremiumRedirect from 'app/router/AlreadyPremiumRedirect';
import LandingFreeTrialPromptRedirect from 'app/router/LandingFreeTrialPromptRedirect';
import Heading from 'app/components/layout/Heading.styled';
import Button from 'app/components/Button/Button';
import Text from 'app/components/layout/Text.styled';
import BasicLink from 'app/components/Links/BasicLink.styled';
import CallToAction from 'app/components/layout/CallToAction.styled';
import {
  productCurrency,
  productPriceAmount,
} from 'app/features/plans/selectors';
import { MIDDLE, BOTTOM } from 'app/analytics/constants/positions';
import { viewPlansCtaSelected } from 'app/features/landing/viewPlansCta/actions';
import { PRODUCT_COMPARISON_ANCHOR_PLANS } from 'app/features/landing/productComparison/constants';
import LocalizedLink from 'app/features/localization/components/LocalizedLink/LocalizedLink';
import { clientReturnUrl } from 'app/features/checkout/selectors';

const UI = ({
  productCurrency,
  productPriceAmount,
  onClick,
  clientReturnUrl,
}) => (
  <S.Gradient>
    <Meta />
    <S.Wrapper>
      <S.YuzuWraper>
        <S.YuzuBackgroundImage
          data-t="yuzu-bacground-image"
          src={`${window.etp.config.assetsUrl}img/registrationPromo/yuzuCircles.png`}
          srcSet={`${window.etp.config.assetsUrl}img/registrationPromo/yuzuCircles@2x.png 2x`}
          alt="YuzuBackground"
        />
        <S.YuzuImage
          data-t="yuzu-image"
          src={`${window.etp.config.assetsUrl}img/registrationPromo/yuzu.png`}
          srcSet={`${window.etp.config.assetsUrl}img/registrationPromo/yuzu@2x.png 2x`}
          alt="Yuzu"
        />
      </S.YuzuWraper>
      <S.Content>
        <Heading size="l">
          <FormattedMessage id="registration_promo_title" />
        </Heading>
        <Benefits />
        <S.Button
          as={ProductCta}
          sku={FAN_PACK_FREE_TRIAL_SKU}
          isWideOnMobile
          position={MIDDLE}
          type="six"
          testId="checkout-cta"
          textKey="buttons_free_trial"
        >
          <S.CallToAction as={CallToAction} size="l">
            <FormattedMessage id="buttons_free_trial" />
          </S.CallToAction>
        </S.Button>
        <S.ButtonNoThanks
          type="three"
          as={Button}
          to={clientReturnUrl}
          testId="skip-checkout-cta"
        >
          <FormattedMessage id="registration_promo_button_no_thanks" />
        </S.ButtonNoThanks>
        <Text size="m">
          <FormattedMessage
            id="registration_promo_description"
            values={{
              price: (
                <FormattedNumber
                  style="currency"
                  currency={productCurrency}
                  value={productPriceAmount}
                />
              ),
              otherPlans: (
                <BasicLink
                  testId="billing-choose-another-plan-link"
                  as={LocalizedLink}
                  onClick={onClick}
                  to={`?upsell=registration-prompt${PRODUCT_COMPARISON_ANCHOR_PLANS}`}
                >
                  <FormattedMessage id="registration_promo_description_click_here" />
                </BasicLink>
              ),
            }}
          />
        </Text>
      </S.Content>
      <S.ChestWraper>
        <S.ChestBackgroundImage
          data-t="chest-bacground-image"
          src={`${window.etp.config.assetsUrl}img/registrationPromo/chestCircles.png`}
          srcSet={`${window.etp.config.assetsUrl}img/registrationPromo/chestCircles@2x.png 2x`}
          alt="ChestBackground"
        />
        <S.ChestImage
          data-t="chest-image"
          src={`${window.etp.config.assetsUrl}img/registrationPromo/chest.png`}
          srcSet={`${window.etp.config.assetsUrl}img/registrationPromo/chest@2x.png 2x`}
          alt="Chest"
        />
      </S.ChestWraper>
    </S.Wrapper>
  </S.Gradient>
);

UI.propTypes = {
  productPriceAmount: PropTypes.string.isRequired,
  productCurrency: PropTypes.string.isRequired,
  clientReturnUrl: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

UI.displayName = 'Registration Premium Prompt';

const onClick = props => () =>
  props.viewPlansCtaSelected({
    upsellType: 'free trial',
    position: BOTTOM,
    textKey: 'registration_promo_description_click_here',
  });

const actions = { viewPlansCtaSelected };

const state = {
  isUserPremium,
  isUserAnonymous,
  isDirectToPay,
  productPriceAmount,
  productCurrency,
  clientReturnUrl,
};

const RegistrationPromo = compose(
  defaultProps({ sku: FAN_PACK_FREE_TRIAL_SKU }),
  connect(state, actions),
  branch(prop('isUserPremium'), renderComponent(AlreadyPremiumRedirect)),
  branch(
    prop('isDirectToPay'),
    renderComponent(LandingFreeTrialPromptRedirect),
  ),
  branch(
    prop('isUserAnonymous'),
    renderComponent(LoginFreeTrialPromptRedirect),
  ),
  withHandlers({ onClick }),
)(UI);

export { UI, onClick };

export default RegistrationPromo;
